import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import AuthLayout, { BreadCrumb, LoginRequired } from 'components/AuthLayout';
import SettingsShell from 'components/settings/SettingsShell';
import { Alert, Box, Button, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { alpha } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { sendApiRequest } from 'lib/api/public';
import notify from 'lib/notify';
import RequireRoles from 'components/common/requireRole';
import Loader from 'components/common/Loader';
import { surfaceCardSx, useInsideSurfaceHost } from 'components/common/surfaces';
import { getTablePreferences, renderPagination } from 'components/utils';
import useUIPagination from 'hooks/useUIPagination';
import SearchInput from './datagrid/SearchInput';
import EmptyPlaceholder from './EmptyPlaceholder';
import ColumnsManager, { ColumnsManagerTab } from './datagrid/ColumnsManager';
import { DataGridColumn } from './datagrid/utils';
import { Cell, CustomInlineEditors } from './datagrid/CellValue';
import RenderRow from './datagrid/RenderRow';
import { OpenInPreference, OpenRecordConfig } from './datagrid/RecordOpenCell';
import { InlineEditingContextProvider } from './datagrid/InlineEditingContext';
import RenderColumns, { getVisibleColumns } from './datagrid/RenderColumns';
import confirm from 'lib/confirm';
import { DeleteConfirmer } from './DeleteConfirmer';

const EMPTY_CELL_VALUES: Record<string, Cell> = {};

function defaultIsRowDisabled<T extends Item>(_object: T) {
  return false;
}

function defaultCellValuesMap<T extends Item>(_item: T, _fetchList: () => void, _search: string): Record<string, Cell> {
  return EMPTY_CELL_VALUES;
}

function defaultOnRowClick<T extends Item>(_object: T, _fetchList: () => void) {
  return undefined;
}

type CustomButtonsProps<T extends Item> = {
  fetchList: () => void;
  selectedObjects: T[];
  setSelectedObjects: (objects: T[]) => void;
  totalCount: number;
  list: T[];
  showForm: () => void;
};

type BivariantCustomButtons<T extends Item> = {
  bivarianceHack(props: CustomButtonsProps<T>): React.ReactNode;
}['bivarianceHack'];

type AddNewComponentProps<T extends Item> = {
  open: boolean;
  object?: T;
  onClose: () => void;
  onSuccess: (response?: unknown) => void;
};

export type Item = {
  _id: string;
};

export type Edit<T extends Item> = {
  disable?: boolean;
  label?: string;
  link?: (object: T) => string;
};

export type AddNew<T extends Item> = {
  label: string;
  component?: React.ComponentType<AddNewComponentProps<T> & Record<string, unknown>>;
  onClick?: () => void;
  beforeClick?: () => void;
  onSuccess?: (response?: unknown, objectId?: string) => void;
  onFormClose?: () => void;
  disabled?: boolean;
  hideWhenSelected?: boolean;
  customProps?: Record<string, unknown>;
  testId?: string;
};

type DataGridProps<T extends Item> = {
  name: string;
  sx?: SxProps;
  rowSx?: SxProps;
  isPage?: boolean;
  /** Use with isPage on /settings routes — wraps in SettingsShell instead of AuthLayout */
  settingsStandalonePage?: boolean;
  pageTitle?: string;
  breadCrumb?: BreadCrumb;
  addNew?: AddNew<T>;
  edit?: Edit<T>;
  testId?: string;
  /** Applied to each body row (e.g. onboarding tours that detect new list items). */
  rowTestId?: string;

  deleteRequestParams?: (objects: T[]) => {
    path: string;
    method: string;
    data: unknown;
  };

  deleteConfirmation?: () => {
    inputConfirmation?: boolean;
    objectType: string;
    objectString: (object: T) => string;
    warning: string;
  };
  bulkDelete?: boolean;
  deleteRoles?: string[];

  // this will be added to the top of the page
  // it will be used for explaining the page
  info?: React.ReactNode;

  endpoint: string;

  // these will be added to the query params for list endpoint
  extraQueryParams?: string;

  // these will be added to the useEffect args
  extraUseEffectArgs?: string[];

  /** When set, rows come from this array instead of calling the list API */
  localList?: T[];

  columns: DataGridColumn[];

  cellValuesMap?: (item: T, fetchList: () => void, search: string) => Record<string, Cell>;

  extraActions?: ({
    selectedObjects,
    setSelectedObjects,
    fetchList,
  }: {
    selectedObjects: T[];
    setSelectedObjects: (objects: T[]) => void;
    fetchList: () => void;
  }) => React.ReactNode;
  extraActionsPosition?: 'left' | 'right';

  tabTitle?: React.ReactNode;
  extraSearchBarContent?: React.ReactNode;
  extraContentAboveTable?: React.ReactNode;

  // these will be added to the right side of the header
  customButtons?: BivariantCustomButtons<T>;

  // there can be multiple DataGrid instances on the same page
  // this is used to differentiate between them
  pageParamName?: string;

  initialSelectedObjects?: T[];
  onLoadList?: (props: { list: T[]; totalCount: number }) => void;
  onRowClick?: (object: T, fetchList: () => void) => void;
  isRowDisabled?: (object: T) => boolean;
  emptyPlaceholder?: React.ReactNode;
  hideSearchInput?: boolean;
  hidePagination?: boolean;
  hideColumnsManager?: boolean;
  columnsManagerShowAsTabs?: boolean;
  columnsManagerTabs?: ColumnsManagerTab[];
  uiPagination?: boolean;
  /** When true, the table grows with its rows and does not use an internal vertical scrollbar. */
  autoHeight?: boolean;

  rowKey?: (object: T, index: number) => string;

  onSearchChange?: (search: string) => void;
  selectedRowId?: string;
  onInlineSave?: (item: T, fieldName: string, value: unknown) => Promise<void>;
  customInlineEditors?: CustomInlineEditors;
  /** When true (default), saves trigger a refetch of the list after the inline edit API call.
   *  Set to false to skip refetch and rely on optimistic cell updates. */
  refetchOnInlineSave?: boolean;
  openRecord?: OpenRecordConfig<T>;
  /** When true, the Options menu includes the per-table Open in choice. */
  showOpenIn?: boolean;
};

function DataGrid<T extends Item>({
  name,
  sx,
  rowSx,
  isPage,
  settingsStandalonePage,
  pageTitle,
  info,
  endpoint,
  pageParamName = 'page',
  deleteRequestParams,
  deleteConfirmation,
  bulkDelete,
  deleteRoles,
  extraQueryParams,
  columns,
  cellValuesMap,
  addNew,
  edit,
  extraActions,
  extraActionsPosition = 'left',
  tabTitle,
  extraSearchBarContent,
  extraContentAboveTable,
  customButtons,
  extraUseEffectArgs,
  localList,
  initialSelectedObjects,
  onLoadList,
  onRowClick,
  isRowDisabled,
  emptyPlaceholder,
  hideSearchInput,
  hidePagination,
  hideColumnsManager,
  columnsManagerShowAsTabs,
  columnsManagerTabs,
  uiPagination,
  autoHeight,
  rowKey,
  breadCrumb,
  onSearchChange,
  selectedRowId,
  testId,
  rowTestId,
  onInlineSave,
  customInlineEditors,
  refetchOnInlineSave = true,
  openRecord,
  showOpenIn = false,
}: DataGridProps<T>) {
  const router = useRouter();
  const insideSurfaceHost = useInsideSurfaceHost();

  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedObjects, setSelectedObjects] = useState<T[]>(initialSelectedObjects || []);

  // currently editing or deleting object
  const [selectedObject, setSelectedObject] = useState<T | null>(null);

  // track column widths for real-time updates during resize
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [columnsReloadKey, setColumnsReloadKey] = useState(0);
  const [topScrollerWidth, setTopScrollerWidth] = useState(0);
  const [showTopScroller, setShowTopScroller] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const topScrollerRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const scrollSyncSourceRef = useRef<'top' | 'table' | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [isLoading, setLoading] = useState(localList === undefined);
  const [list, setList] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [openIn, setOpenIn] = useState<OpenInPreference>('sidePanel');
  const pageNumber = (router.query[pageParamName] as string) || '1';
  const massImportId = (router.query.massImportId as string) || '';

  useEffect(() => {
    const saved = getTablePreferences(name).openIn;
    setOpenIn(saved === 'fullPage' ? 'fullPage' : 'sidePanel');
  }, [name]);
  const isRowDisabledFn = isRowDisabled || defaultIsRowDisabled;
  const cellValuesMapFn = cellValuesMap || defaultCellValuesMap;
  const onRowClickFn = onRowClick || defaultOnRowClick;

  const { paginatedList, renderPagination: renderUIPagination } = useUIPagination({
    list,
    pageParamName: `${name}Page`,
  });

  const onLoadListRef = useRef(onLoadList);
  onLoadListRef.current = onLoadList;

  // `silent` refetches in the background without flashing the loading state — used by
  // pollers (e.g. interview transcription) so the table doesn't blink every few seconds.
  const fetchList = useCallback(
    (silent = false) => {
      if (localList !== undefined) {
        setLoading(false);
        setList(localList);
        setTotalCount(localList.length);
        onLoadListRef.current?.({ list: localList, totalCount: localList.length });
        return;
      }

      const tablePreferences = getTablePreferences(name);
      const search = (tablePreferences.search || '') as string;
      const sortField = (tablePreferences.sortField || '') as string;
      const sortDirection = (tablePreferences.sortDirection || '') as string;

      if (!silent) {
        setLoading(true);
      }

      sendApiRequest<{ totalCount?: number; list?: T[] } | T[]>({
        path: `/${endpoint}?page=${pageNumber}&search=${encodeURIComponent(search)}&sortField=${encodeURIComponent(sortField)}&sortDirection=${encodeURIComponent(sortDirection)}&massImportId=${encodeURIComponent(massImportId)}&${extraQueryParams}`,
        method: 'GET',
      })
        .then(response => {
          setLoading(false);

          let totalCount = 0;
          let nextList: T[] = [];

          if (Array.isArray(response)) {
            nextList = response;
            totalCount = response.length;
            setTotalCount(totalCount);
            setList(nextList);
          } else {
            if (typeof response.totalCount !== 'undefined') {
              totalCount = response.totalCount;
              setTotalCount(totalCount);
            }

            if (response.list) {
              nextList = response.list;
              setList(nextList);
            }
          }

          onLoadListRef.current?.({ list: nextList, totalCount });
        })
        .catch(e => {
          notify(e);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [localList, name, endpoint, pageNumber, massImportId, extraQueryParams],
  );

  const debouncedFetchList = useMemo(() => debounce(fetchList, 1500), [fetchList]);
  useEffect(() => () => debouncedFetchList.clear(), [debouncedFetchList]);

  const deleteObjects = async (objects: T[]) => {
    if (!deleteRequestParams) {
      return;
    }

    setIsDeleting(true);

    try {
      await sendApiRequest({
        ...deleteRequestParams(objects),
      });

      notify('Deleted successfully');
      fetchList();
    } catch (error) {
      notify(error);
    } finally {
      setSelectedObjects([]);
      setIsDeleting(false);
    }
  };

  const getList = () => {
    if (uiPagination) {
      return paginatedList;
    }

    return list;
  };

  useEffect(() => {
    fetchList();
  }, [pageNumber, massImportId, localList, ...(extraUseEffectArgs || [])]);

  const getColumns = useCallback(() => getVisibleColumns({ tableName: name, columns }), [name, columns]);
  const syncTopScrollerMetrics = useCallback(() => {
    const tableContainer = tableContainerRef.current;
    const topScroller = topScrollerRef.current;

    if (!tableContainer) {
      setTopScrollerWidth(0);
      setShowTopScroller(false);
      return;
    }

    const tableScrollWidth = tableRef.current?.scrollWidth || tableContainer.scrollWidth;
    const { clientWidth } = tableContainer;
    const hasHorizontalOverflow = tableScrollWidth > clientWidth + 1;
    setTopScrollerWidth(tableScrollWidth);
    setShowTopScroller(hasHorizontalOverflow);

    if (topScroller && topScroller.scrollLeft !== tableContainer.scrollLeft) {
      topScroller.scrollLeft = tableContainer.scrollLeft;
    }
  }, []);

  const handleTopScrollerScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (scrollSyncSourceRef.current === 'table') {
      scrollSyncSourceRef.current = null;
      return;
    }

    const tableContainer = tableContainerRef.current;
    const nextScrollLeft = event.currentTarget.scrollLeft;

    if (tableContainer && tableContainer.scrollLeft !== nextScrollLeft) {
      scrollSyncSourceRef.current = 'top';
      tableContainer.scrollLeft = nextScrollLeft;
    }
  };

  const handleTableScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (scrollSyncSourceRef.current === 'top') {
      scrollSyncSourceRef.current = null;
      return;
    }

    const nextScrollLeft = event.currentTarget.scrollLeft;
    const topScroller = topScrollerRef.current;

    if (topScroller && topScroller.scrollLeft !== nextScrollLeft) {
      scrollSyncSourceRef.current = 'table';
      topScroller.scrollLeft = nextScrollLeft;
    }
  };

  useEffect(() => {
    const rafId = window.requestAnimationFrame(() => {
      syncTopScrollerMetrics();
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [syncTopScrollerMetrics, list, paginatedList, uiPagination, columnWidths, columnsReloadKey]);

  useEffect(() => {
    window.addEventListener('resize', syncTopScrollerMetrics);
    return () => {
      window.removeEventListener('resize', syncTopScrollerMetrics);
    };
  }, [syncTopScrollerMetrics]);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    const table = tableRef.current;

    if (!tableContainer || !table || typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      syncTopScrollerMetrics();
    });

    resizeObserver.observe(tableContainer);
    resizeObserver.observe(table);

    return () => {
      resizeObserver.disconnect();
    };
  }, [syncTopScrollerMetrics, showTopScroller]);

  const renderForm = () => {
    if (!showForm || !addNew || !addNew.component) {
      return null;
    }

    const Component = addNew.component;

    return (
      <Component
        open={showForm}
        object={selectedObject || undefined}
        onClose={() => {
          setShowForm(false);

          if (addNew.onFormClose) {
            addNew.onFormClose();
          }
        }}
        onSuccess={response => {
          if (selectedObject?._id) {
            notify('Saved successfully');
          } else {
            notify('Added successfully');
          }

          setSelectedObject(null);
          setSelectedObjects([]);
          setShowForm(false);

          fetchList();

          if (addNew.onSuccess) {
            addNew.onSuccess(response, selectedObject?._id);
          }
        }}
        {...(addNew.customProps || {})}
      />
    );
  };

  const renderInfo = () => {
    if (!info) {
      return null;
    }

    return (
      <Alert severity="info" sx={{ marginBottom: '10px', flexShrink: 0 }}>
        {info}
      </Alert>
    );
  };

  const deleteObject = async (object: T) => {
    if (!deleteRequestParams) {
      return;
    }

    setIsDeleting(true);
    try {
      await sendApiRequest({
        ...deleteRequestParams([object]),
      });

      notify('Deleted successfully');
      fetchList();
    } catch (error) {
      notify(error);
    } finally {
      setSelectedObjects([]);
      setIsDeleting(false);
    }
  };

  const renderDeleteBulkButton = () => {
    if (!deleteRequestParams || !deleteConfirmation) {
      return null;
    }

    let title = `Are you sure to delete ${selectedObjects.length} ${deleteConfirmation().objectType}s`;

    if (selectedObjects.length === 1) {
      title = `Are you sure to delete "${deleteConfirmation().objectString(selectedObjects[0])}"?`;
    }

    const deleteButton = (
      <Button
        variant="contained"
        color="error"
        disabled={isDeleting}
        onClick={() => {
          confirm({
            type: 'error',
            title,
            message: `
              ${deleteConfirmation().warning}
            `,
            onAnswer: answer => {
              if (answer) {
                void deleteObjects(selectedObjects);
              }
            },
            buttonText: 'Delete',
          });
        }}
      >
        Delete
      </Button>
    );

    return deleteRoles ? <RequireRoles roles={deleteRoles}>{deleteButton}</RequireRoles> : deleteButton;
  };

  const renderDeleteButton = (object: T) => {
    if (!deleteRequestParams || !deleteRequestParams([object])) {
      return null;
    }

    if (!deleteConfirmation) {
      return null;
    }

    let title = `Are you sure to delete ${deleteConfirmation().objectType}`;

    if (deleteConfirmation().objectString) {
      title = `Are you sure to delete ${deleteConfirmation().objectType} "${deleteConfirmation().objectString(object)}"?`;
    }

    const deleteButton = (
      <Button variant="contained" color="error">
        <DeleteConfirmer
          isDeleting={isDeleting}
          text="Delete"
          title={title}
          message={deleteConfirmation().warning}
          onDelete={() => void deleteObject(object)}
          size="sm"
          inputConfirmation={deleteConfirmation().inputConfirmation}
        />
      </Button>
    );

    return deleteRoles ? <RequireRoles roles={deleteRoles}>{deleteButton}</RequireRoles> : deleteButton;
  };

  const renderEditButton = (object: T) => {
    if (edit?.disable) {
      return null;
    }

    if (addNew && addNew.component) {
      return (
        <Button
          onClick={() => {
            setSelectedObject(object);
            setShowForm(true);
          }}
          variant="contained"
          color="inherit"
        >
          Edit
        </Button>
      );
    }

    if (edit && edit.link) {
      return (
        <Button variant="contained" color="inherit" href={edit.link(object)}>
          Edit
        </Button>
      );
    }

    return null;
  };

  const renderActions = () => {
    if (selectedObjects.length === 0) {
      return null;
    }

    const buttons: React.ReactNode[] = [];

    if (selectedObjects.length === 1) {
      const editButton = renderEditButton(selectedObjects[0]);

      if (editButton) {
        buttons.push(editButton);
      }
    }

    if (bulkDelete) {
      const bulkDeleteButton = renderDeleteBulkButton();

      if (bulkDeleteButton) {
        buttons.push(bulkDeleteButton);
      }
    } else {
      if (selectedObjects.length === 1) {
        const deleteButton = renderDeleteButton(selectedObjects[0]);

        if (deleteButton) {
          buttons.push(deleteButton);
        }
      }
    }

    let extraActionsContent = null;

    if (extraActions) {
      extraActionsContent = extraActions({ selectedObjects, fetchList, setSelectedObjects });
    }

    if (buttons.length === 0 && !extraActionsContent) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flex: 1,
          flexWrap: 'wrap',
          whiteSpace: 'nowrap',
        }}
      >
        {extraActionsPosition === 'left' && extraActionsContent}
        {buttons.map(button => button)}
        {extraActionsPosition === 'right' && extraActionsContent}
      </Box>
    );
  };

  const renderContent = () => {
    const hasRightSideButtons = customButtons || (addNew && (addNew.component || addNew.onClick));
    const actionsContent = renderActions();

    const tableSx: SxProps = [
      autoHeight
        ? {
            flex: '0 0 auto',
            overflowY: 'visible',
            borderRadius: 2,
          }
        : {
            // Size to rows; shrink + scroll when taller than remaining viewport space.
            flex: '0 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            borderRadius: 2,
          },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ];

    // Single paper fill everywhere: either this host paints it, or an ancestor
    // TabSubBlock/Group already did (same token). Never nest two paper cards.
    // Size to content (no stretched empty paper); by default cap at parent so long
    // lists scroll inside the table. autoHeight grows with rows instead.
    const rootSx = {
      ...(insideSurfaceHost ? {} : surfaceCardSx),
      ...(insideSurfaceHost ? {} : { p: 2 }),
      ...(autoHeight
        ? {
            flex: '0 0 auto',
            alignSelf: 'stretch',
            width: '100%',
            display: 'flex',
            flexDirection: 'column' as const,
            overflow: 'visible',
          }
        : {
            flex: '0 1 auto',
            alignSelf: 'stretch',
            width: '100%',
            minHeight: 0,
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column' as const,
            overflow: 'hidden',
          }),
    };

    return (
      <InlineEditingContextProvider>
        <Box sx={rootSx}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              marginBottom: 2,
              maxWidth: '100%',
              flexWrap: 'wrap',
              gap: 1,
              flexShrink: 0,
            }}
          >
            {tabTitle && (
              <Typography variant="h6" sx={{ fontSize: '16px' }}>
                {tabTitle}
              </Typography>
            )}

            {!hideSearchInput && (
              <SearchInput
                tableName={name}
                pageParamName={pageParamName}
                onChange={search => {
                  fetchList();

                  if (onSearchChange) {
                    onSearchChange(search);
                  }
                }}
              />
            )}
            {extraSearchBarContent}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                ml: 'auto',
                columnGap: 1,
                rowGap: 1,
              }}
            >
              {actionsContent ? (
                <Box
                  sx={{
                    display: 'flex',
                    flex: { lg: 1 },
                    borderRight: theme => {
                      if (!hasRightSideButtons) {
                        return 'none';
                      }

                      const dividerColor =
                        theme.palette.mode === 'dark'
                          ? `1px solid ${alpha(theme.palette.common.white, 0.35)}`
                          : `1px solid ${theme.palette.divider}`;

                      return { xs: 'none', sm: dividerColor };
                    },
                    pr: hasRightSideButtons ? { xs: 1 } : 0,
                  }}
                >
                  {actionsContent}
                </Box>
              ) : null}

              {!hideColumnsManager && (
                <ColumnsManager
                  name={name}
                  initialColumns={columns}
                  fetchList={fetchList}
                  reloadKey={columnsReloadKey}
                  showAsTabs={columnsManagerShowAsTabs}
                  tabs={columnsManagerTabs}
                  showOpenIn={showOpenIn}
                  onOpenInChange={setOpenIn}
                />
              )}

              {customButtons && (
                <Box
                  sx={{
                    display: 'contents',
                    '& > *': { display: 'contents' },
                  }}
                >
                  {customButtons({
                    fetchList,
                    selectedObjects,
                    setSelectedObjects,
                    totalCount,
                    list: getList(),
                    showForm: () => setShowForm(true),
                  })}
                </Box>
              )}

              {addNew && addNew.component && !(addNew.hideWhenSelected && selectedObjects.length > 0) && (
                <Button
                  data-testid={addNew.testId}
                  onClick={() => {
                    if (addNew.beforeClick) {
                      addNew.beforeClick();
                    }

                    setShowForm(true);
                    setSelectedObjects([]);
                    setSelectedObject(null);
                  }}
                  variant="contained"
                  color="primary"
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  disabled={addNew.disabled}
                >
                  {addNew.label}
                </Button>
              )}

              {addNew && addNew.onClick && !(addNew.hideWhenSelected && selectedObjects.length > 0) && (
                <Button
                  onClick={addNew.onClick}
                  variant="contained"
                  color="primary"
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {addNew.label}
                </Button>
              )}
            </Box>
          </Box>
          {renderInfo()}
          {renderForm()}

          {extraContentAboveTable ? <Box sx={{ flexShrink: 0 }}>{extraContentAboveTable}</Box> : null}
          {getList().length > 0 && (
            <Box
              sx={{
                // Don't grow past row height so pagination stays under the table;
                // still shrink so long lists scroll inside the table only.
                ...(autoHeight ? { flex: '0 0 auto' } : { flex: '0 1 auto', minHeight: 0 }),
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {showTopScroller && (
                <Box
                  ref={topScrollerRef}
                  onScroll={handleTopScrollerScroll}
                  sx={{
                    mt: 0,
                    mb: 0,
                    lineHeight: 0,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    height: 12,
                    flexShrink: 0,
                    '&::-webkit-scrollbar': {
                      height: 10,
                    },
                  }}
                >
                  <Box sx={{ width: `${topScrollerWidth}px`, height: 1 }} />
                </Box>
              )}
              <TableContainer ref={tableContainerRef} onScroll={handleTableScroll} sx={tableSx} data-testid={testId}>
                <Table
                  ref={tableRef}
                  size="small"
                  sx={{
                    width: 'auto',
                    '& td, & th': { px: 0.75 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <RenderColumns<T>
                        tableName={name}
                        columns={columns}
                        list={getList()}
                        selectedObjects={selectedObjects}
                        setSelectedObjects={setSelectedObjects}
                        fetchList={fetchList}
                        columnWidths={columnWidths}
                        setColumnWidths={setColumnWidths}
                        onColumnsChange={() => {
                          setColumnsReloadKey(prev => prev + 1);
                        }}
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getList().map((item, index) => (
                      <RenderRow<T>
                        key={rowKey ? rowKey(item, index) : item._id || index}
                        rowTestId={rowTestId}
                        sx={rowSx}
                        tableName={name}
                        selectedObjects={selectedObjects}
                        setSelectedObjects={setSelectedObjects}
                        isRowDisabled={isRowDisabledFn}
                        cellValuesMap={cellValuesMapFn}
                        getColumns={getColumns}
                        fetchList={fetchList}
                        onCellSaved={refetchOnInlineSave !== false ? debouncedFetchList : undefined}
                        item={item}
                        onRowClick={onRowClickFn}
                        selectedRowId={selectedRowId}
                        columnWidths={columnWidths}
                        onInlineSave={onInlineSave}
                        customInlineEditors={customInlineEditors}
                        openRecord={openRecord}
                        openIn={openIn}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <div className="clear" />

          <Box display="flex" justifyContent="flex-start" alignItems="center" sx={{ flexShrink: 0 }}>
            {!hidePagination && getList().length > 0 && (
              <>
                {uiPagination
                  ? renderUIPagination()
                  : renderPagination({
                      router,
                      totalCount,
                      pageParamName,
                      page: parseInt(pageNumber, 10),
                      extraQueryParams: {},
                    })}
              </>
            )}
          </Box>

          {!isLoading && getList().length === 0 && <EmptyPlaceholder text={emptyPlaceholder} />}
        </Box>
      </InlineEditingContextProvider>
    );
  };

  if (isPage) {
    const pageBody = (
      <>
        <Head>
          {!settingsStandalonePage ? <title>{pageTitle || breadCrumb?.titleText || breadCrumb?.title}</title> : null}
          <meta name="description" content={breadCrumb?.titleText || ''} />
        </Head>

        {renderContent()}
      </>
    );

    if (settingsStandalonePage) {
      return (
        <LoginRequired>
          <SettingsShell breadCrumb={breadCrumb} isLoading={isLoading}>
            {pageBody}
          </SettingsShell>
        </LoginRequired>
      );
    }

    return (
      <LoginRequired>
        <AuthLayout breadCrumb={breadCrumb} isLoading={isLoading}>
          {pageBody}
        </AuthLayout>
      </LoginRequired>
    );
  }

  return (
    <>
      {isLoading && <Loader />}
      {renderContent()}
    </>
  );
}

export default DataGrid;
